import { useState, type FormEvent } from 'react'

import { t } from '@ctcells/i18n'

import { TextInput } from '../../atoms/TextInput.tsx'
import type { ActionGate } from '../access.ts'

import { fromInputText, toInputText } from './policyModel.ts'

/**
 * 정책 숫자 한 칸 — **Enter 나 칸을 떠날 때 바로 나간다.** 「변경」·「저장」 단추가 없다
 * (eric 2026-09-22 「저장 버튼 넣지 말고, 값 바꾸면 해당 값이 적용되게」 · `settingsRows.tsx` 머리말).
 *
 * - 빈 칸으로 두고 떠나면 `null` = **지움**(윗단 · 기본값을 따른다). 따르는 값은 placeholder 가 말한다
 * - 받은 값과 같으면 보내지 않는다
 * - 1 이상의 정수(서버 단위)가 아니면 보내지 않고 칸 옆에 까닭을 단다
 *
 * 🔴 **낙관적 갱신이 없다**(DP-04). 칸의 글자는 친 동안만 사람 것이고, 서버 값이 바뀌거나 거절이 오면
 *    **서버 값으로 되돌아간다** — 거절된 값을 칸에 남겨 「적용됐다」로 읽히게 두지 않는다. 사유는 구획이 단다.
 *
 * @param value 그 단에 적힌 값(서버 단위). `null` 이면 안 정함 → 빈 칸
 * @param scale 서버 단위 / 화면 단위(세션은 분이라 60 · 나머지 1)
 * @param failedAt 이 칸이 실린 마지막 요청이 거절된 횟수표 — 바뀌면 글자를 서버 값으로 되돌린다
 */
export interface PolicyNumberInputProps {
  label: string
  value: number | null
  scale?: number | undefined
  unit: string
  placeholder: string
  gate: ActionGate
  failedAt?: number | undefined
  onCommit: (next: number | null) => void
}

export function PolicyNumberInput({
  label,
  value,
  scale = 1,
  unit,
  placeholder,
  gate,
  failedAt,
  onCommit,
}: PolicyNumberInputProps) {
  const shown = toInputText(value, scale)
  const [draft, setDraft] = useState(shown)
  const [invalid, setInvalid] = useState(false)
  // 서버 값 · 거절이 바뀐 그 그리기에서 글자를 되돌린다(효과 안 `setState` 를 쓰지 않는다)
  const [seen, setSeen] = useState({ shown, failedAt })
  if (seen.shown !== shown || seen.failedAt !== failedAt) {
    setSeen({ shown, failedAt })
    setDraft(shown)
    setInvalid(false)
  }

  const commit = () => {
    if (gate.disabled) return
    const next = fromInputText(draft, scale)
    if (next === 'invalid') {
      setInvalid(true)
      return
    }
    setInvalid(false)
    if (next === value) {
      setDraft(shown)
      return
    }
    onCommit(next)
  }

  return (
    <form
      className="policy-input"
      onBlur={commit}
      onSubmit={(event: FormEvent) => {
        event.preventDefault()
        commit()
      }}
    >
      <TextInput
        aria-label={label}
        inputMode="decimal"
        invalid={invalid}
        onChange={setDraft}
        placeholder={placeholder}
        value={draft}
        {...gate}
      />
      <span className="policy-input__unit">{unit}</span>
      {invalid ? (
        <span className="gset__err" role="alert">
          {t('policy.invalidNumber')}
        </span>
      ) : null}
    </form>
  )
}
